import { tr } from 'i18n';
import { useTranslation as useInterfaceLanguage } from 'react-i18next';
import React, { useState, useEffect } from 'react'
import { createPortal } from 'react-dom'
import styles from './DocPreviewModal.module.scss'
import { fmtSize, fileIcon } from '../../utils'
import { IconDownload, IconClose } from '../../icons'

const IMAGE_EXTS  = new Set(['jpg','jpeg','png','gif','webp','svg','bmp','ico'])
const OFFICE_EXTS = new Set(['doc','docx','xls','xlsx','ppt','pptx','odt','ods','odp'])
const TEXT_EXTS   = new Set(['txt','csv','json','xml','log','md','yaml','ini','toml','sh','bat'])

export default function DocPreviewModal({ doc, onClose, onDownload }) {
  useInterfaceLanguage();
  const [textContent, setTextContent] = useState(null)
  const [textLoading, setTextLoading] = useState(false)
  const [textError,   setTextError]   = useState(false)

  const ext     = (doc.file_extension || doc.file_name?.split('.').pop() || '').toLowerCase()
  const fileUrl = doc.file_url || doc.file || ''
  const isImage  = IMAGE_EXTS.has(ext)
  const isPdf    = ext === 'pdf'
  const isOffice = OFFICE_EXTS.has(ext)
  const isText   = TEXT_EXTS.has(ext)

  useEffect(() => {
    const fn = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('keydown', fn)
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', fn)
      document.body.style.overflow = ''
    }
  }, [onClose])

  useEffect(() => {
    if (!isText || !fileUrl) return
    setTextLoading(true)
    fetch(fileUrl)
      .then(r => r.text())
      .then(t => { setTextContent(t); setTextLoading(false) })
      .catch(() => { setTextError(true); setTextLoading(false) })
  }, [fileUrl, isText])

  const googleViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(fileUrl)}&embedded=true`

  const renderBody = () => {
    if (!fileUrl) {
      return (
        <div className={styles.unsupported}>
          <span className={styles.unsupportedEmoji}>⚠️</span>
          <p className={styles.unsupportedTitle}>{tr("Ссылка на файл недоступна")}</p>
          <button className={styles.dlBtn2} onClick={onDownload}><IconDownload />{tr(" Скачать")}</button>
        </div>
      )
    }
    if (isImage) {
      return (
        <div className={styles.imageWrap}>
          <img src={fileUrl} className={styles.img} alt={doc.title || doc.file_name} />
        </div>
      )
    }
    if (isPdf) {
      return <iframe src={fileUrl} className={styles.frame} title={doc.title || doc.file_name} />
    }
    if (isOffice) {
      return (
        <iframe
          src={googleViewerUrl}
          className={styles.frame}
          title={doc.title || doc.file_name}
        />
      )
    }
    if (isText) {
      if (textLoading) return (
        <div className={styles.loading}>
          <div className={styles.spinner} />
          <p>{tr("Загрузка…")}</p>
        </div>
      )
      if (textError || textContent === null) return (
        <div className={styles.unsupported}>
          <span className={styles.unsupportedEmoji}>⚠️</span>
          <p className={styles.unsupportedTitle}>{tr("Не удалось прочитать файл")}</p>
          <button className={styles.dlBtn2} onClick={onDownload}><IconDownload />{tr(" Скачать")}</button>
        </div>
      )
      return (
        <div className={styles.textWrap}>
          <pre className={styles.text}>{tr(textContent)}</pre>
        </div>
      )
    }
    return <iframe src={fileUrl} className={styles.frame} title={doc.title || doc.file_name} />
  }

  return createPortal(
    <div
      className={styles.overlay}
      onMouseDown={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className={styles.modal}>
        <div className={styles.header}>
          <div className={styles.titleRow}>
            <span className={styles.emoji}>{fileIcon(ext)}</span>
            <span className={styles.title}>{tr(doc.title || doc.file_name)}</span>
            {tr(ext && <span className={styles.extBadge}>{ext.toUpperCase()}</span>)}
            {tr(doc.file_size > 0 && <span className={styles.size}>{fmtSize(doc.file_size)}</span>)}
          </div>
          <div className={styles.headerActions}>
            <button className={styles.dlBtn} onClick={onDownload} title={tr("Скачать")}>
              <IconDownload />{tr(" Скачать")}</button>
            <button className={styles.closeBtn} onClick={onClose} title={tr("Закрыть")}>
              <IconClose />
            </button>
          </div>
        </div>
        <div className={styles.body}>{renderBody()}</div>
      </div>
    </div>,
    document.body
  )
}
